import { useEffect, useMemo, useState } from 'react'

import { AxiosResponse } from 'axios'

import { SearchRequestData } from '../../../models/searchForCare/SearchForCare'
import { SpeculativeFilterObject } from '../../wellnessToolkit/types'
import { getSpeculativeSearchDefaultCoachingSearchParams } from '../utils'

export interface useRunSpeculativeSearchArgs {
  searchRequestData?: Partial<SearchRequestData>
  isUserLoggedIn: boolean
  lyraId: string
  userDOB: string | undefined
  userCountryName: string | undefined
  isInternationalUser: boolean
  postSpeculativeSearch: (lyraId: string, searchData: SearchRequestData) => Promise<AxiosResponse<boolean>>
  isSpeculativeFilterEnabled: boolean
  filters?: SpeculativeFilterObject
}

/**
 * Check if there's an available supply of providers given a specific search
 *
 * @param searchRequestData If undefined, no speculative search will be made
 * @returns {boolean} hasAvailableSupply
 * @returns {boolean} isLoading
 * @returns {boolean} hasError True when the most recent speculative request failed; `hasAvailableSupply` is
 *   `false` in that case, so consumers that must distinguish "no supply" from "unknown" should check this first.
 */
export const useRunSpeculativeSearch = ({
  searchRequestData,
  isUserLoggedIn,
  lyraId,
  userDOB,
  userCountryName,
  isInternationalUser,
  postSpeculativeSearch,
  isSpeculativeFilterEnabled,
  filters,
}: useRunSpeculativeSearchArgs): { hasAvailableSupply: boolean; isLoading: boolean; hasError: boolean } => {
  const request = useMemo(() => {
    if (!isUserLoggedIn || !searchRequestData) {
      return undefined
    }
    return {
      lyraId,
      searchRequestData,
      userDOB,
      userCountryName,
      isInternationalUser,
      isSpeculativeFilterEnabled,
      filters,
      postSpeculativeSearch,
    }
  }, [
    isUserLoggedIn,
    searchRequestData,
    lyraId,
    isInternationalUser,
    userDOB,
    userCountryName,
    isSpeculativeFilterEnabled,
    filters,
    postSpeculativeSearch,
  ])
  const [result, setResult] = useState<{
    request: NonNullable<typeof request>
    hasAvailableSupply: boolean
    hasError: boolean
  }>()

  useEffect(() => {
    const currentRequest = request
    if (!currentRequest) {
      return
    }
    let isCurrent = true
    async function fetchData(activeRequest: NonNullable<typeof request>) {
      const { isInternationalUser, userDOB, userCountryName, isSpeculativeFilterEnabled, filters, searchRequestData } =
        activeRequest
      const searchData: SearchRequestData = {
        ...getSpeculativeSearchDefaultCoachingSearchParams({
          isInternationalUser,
          userDOB,
          userCountryName,
          isSpeculativeFilterEnabled,
        }),
        ...searchRequestData,
        ...(isSpeculativeFilterEnabled && filters),
      }
      try {
        const response = await activeRequest.postSpeculativeSearch(activeRequest.lyraId, searchData)
        if (isCurrent) {
          setResult({ request: activeRequest, hasAvailableSupply: response.data, hasError: false })
        }
      } catch (e) {
        console.error(e)
        if (isCurrent) {
          setResult({ request: activeRequest, hasAvailableSupply: false, hasError: true })
        }
      }
    }
    void fetchData(currentRequest)
    return () => {
      isCurrent = false
    }
  }, [request])

  // A newly eligible seat must stay loading even before the effect starts its search.
  const currentResult = result?.request === request ? result : undefined
  return {
    hasAvailableSupply: currentResult?.hasAvailableSupply ?? false,
    isLoading: Boolean(request && !currentResult),
    hasError: currentResult?.hasError ?? false,
  }
}
